import { useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ScrollSmoother from "gsap/ScrollSmoother";
import SplitText from "gsap/SplitText";

gsap.registerPlugin(ScrollTrigger, ScrollSmoother, SplitText);

export default function AnimationProvider() {
   // Bootstrap JS is now imported in main.tsx

  useEffect(() => {
    if (typeof window === "undefined") return;
 
    // Detect mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
    
    let smoother: ScrollSmoother; 
    let ctx: gsap.Context;
    let periodicFix: NodeJS.Timeout | null = null;

    const initAnimations = () => {
      ctx = gsap.context(() => {
        // Initialize ScrollSmoother only on desktop
        if (!isMobile && !ScrollSmoother.get()) {
          try {
            smoother = ScrollSmoother.create({
              smooth: 1.75,
              effects: true,
              smoothTouch: false,
              normalizeScroll: false,
              ignoreMobileResize: true,
              wrapper: "#smooth-wrapper",
              content: "#smooth-content",
            });
          } catch (error) {
            console.warn("ScrollSmoother initialization failed:", error);
          }
        }

        // Wait for fonts to load and DOM to be ready
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => {
            setTimeout(createAnimations, 100);
          });
        } else {
          setTimeout(createAnimations, 500);
        }
      });
    };

    const createAnimations = () => {
      // في حالة اللغة العربية/اتجاه RTL نتجنب استخدام SplitText لتفادي تباعد الحروف
      const isRTL = document.documentElement.dir === "rtl" || document.documentElement.lang === "ar";
      if (isRTL) {
        return;
      }
      // === Heading Chars ===
      gsap.utils.toArray<HTMLElement>(".heading-chars").forEach((el) => {
        if (!el.querySelector(".gsap-split-char")) {
          const split = new SplitText(el, { type: "chars", smartWrap: true });
          
          // إزالة letter-spacing من العناصر المنشأة ديناميكياً - تطبيق شامل
          const fixSpacing = (element: HTMLElement) => {
            if (document.documentElement.dir === "rtl" || document.documentElement.lang === "ar") {
              element.style.setProperty("letter-spacing", "normal", "important");
              element.style.setProperty("word-spacing", "normal", "important");
              // إزالة من جميع العناصر الداخلية
              element.querySelectorAll("*").forEach((child) => {
                const htmlChild = child as HTMLElement;
                htmlChild.style.setProperty("letter-spacing", "normal", "important");
                htmlChild.style.setProperty("word-spacing", "normal", "important");
              });
            }
          };
          
          split.chars.forEach((char) => {
            const htmlChar = char as HTMLElement;
            fixSpacing(htmlChar);
            // إصلاح فوري بعد إنشاء العنصر
            setTimeout(() => fixSpacing(htmlChar), 0);
          });
          
          // إصلاح العنصر الأصلي أيضاً
          fixSpacing(el);
          
          gsap.fromTo(
            split.chars,
            { y: 20, autoAlpha: 0 },
            {
              y: 0,
              autoAlpha: 1,
              scrollTrigger: {
                trigger: el,
                start: "top 95%",
                toggleActions: "play none none none",
              },
              stagger: 0.05,
              duration: 0.5,
              ease: "power2.out",
              onComplete: () => {
                // إعادة إصلاح بعد اكتمال الأنيميشن
                split.chars.forEach((char) => fixSpacing(char as HTMLElement));
              }
            }
          );
        }
      });

      // === Heading Words ===
      gsap.utils.toArray<HTMLElement>(".heading-word").forEach((el) => {
        if (!el.querySelector(".gsap-split-word")) {
          const delay = parseFloat(el.dataset.delay || "0");
          const split = new SplitText(el, { type: "words" });

          // إزالة letter-spacing من العناصر المنشأة ديناميكياً
          split.words.forEach((word) => {
            const htmlWord = word as HTMLElement;
            htmlWord.style.letterSpacing = "normal";
            htmlWord.style.wordSpacing = "normal";
            // إزالة letter-spacing من جميع العناصر الداخلية
            htmlWord.querySelectorAll("span").forEach((span) => {
              const htmlSpan = span as HTMLElement;
              htmlSpan.style.letterSpacing = "normal";
              htmlSpan.style.wordSpacing = "normal";
            });
          });

          gsap.fromTo(
            split.words,
            { y: 60, autoAlpha: 0 },
            {
              y: 0,
              autoAlpha: 1,
              scrollTrigger: {
                trigger: el,
                start: "top 95%",
                toggleActions: "play none none none",
              },
              stagger: 0.1,
              delay,
              duration: 0.6,
              ease: "power2.out",
            }
          );
        }
      });

      // === Heading Lines ===
      gsap.utils.toArray<HTMLElement>(".heading-line").forEach((el) => {
        if (!el.querySelector(".gsap-split-line")) {
          const delay = parseFloat(el.dataset.delay || "0");
          const split = new SplitText(el, { type: "lines" });

          // إزالة letter-spacing من العناصر المنشأة ديناميكياً
          split.lines.forEach((line) => {
            const htmlLine = line as HTMLElement;
            htmlLine.style.letterSpacing = "normal";
            htmlLine.style.wordSpacing = "normal";
            // إزالة letter-spacing من جميع العناصر الداخلية
            htmlLine.querySelectorAll("span").forEach((span) => {
              const htmlSpan = span as HTMLElement;
              htmlSpan.style.letterSpacing = "normal";
              htmlSpan.style.wordSpacing = "normal";
            });
          });

          gsap.fromTo(
            split.lines,
            { y: 80, autoAlpha: 0 },
            {
              y: 0,
              autoAlpha: 1,
              scrollTrigger: {
                trigger: el,
                start: "top 95%",
                toggleActions: "play none none none",
              },
              stagger: 0.15,
              delay,
              duration: 0.7,
              ease: "power2.out",
            }
          );
        }
      });

      // === Color Change ===
      gsap.utils.toArray<HTMLElement>(".color-change").forEach((el) => {
        if (!el.querySelector(".gsap-split-char")) {
          const split = new SplitText(el, { type: "chars", smartWrap: true });
          
          // إزالة letter-spacing من العناصر المنشأة ديناميكياً
          split.chars.forEach((char) => {
            const htmlChar = char as HTMLElement;
            htmlChar.style.letterSpacing = "normal";
            htmlChar.style.wordSpacing = "normal";
          });
          
          gsap.fromTo(
            split.chars,
            { color: "#c1a360" },
            {
              color: "#3147FF",
              stagger: 0.05,
              scrollTrigger: {
                trigger: el,
                start: "top 95%",
                scrub: true,
              },
            }
          );
        }
      });

      // === Fade In Up ===
      gsap.utils.toArray<HTMLElement>(".fadeInUp").forEach((el) => {
        const delay = parseFloat(el.dataset.delay || "0");
        // على الموبايل، نستخدم animations أبسط
        if (isMobile) {
          gsap.fromTo(
            el,
            { autoAlpha: 0 },
            {
              autoAlpha: 1,
              scrollTrigger: {
                trigger: el,
                start: "top 95%",
                toggleActions: "play none none none",
                once: true, // تشغيل مرة واحدة فقط
              },
              duration: 0.5,
              delay,
              ease: "power2.out",
            }
          );
        } else {
          gsap.fromTo(
            el,
            { y: 60, autoAlpha: 0 },
            {
              y: 0,
              autoAlpha: 1,
              scrollTrigger: {
                trigger: el,
                start: "top 95%",
                toggleActions: "play none none none",
              },
              duration: 0.8,
              delay,
              ease: "power2.out",
            }
          );
        }
      });
      
      // === TranslateY10 === (مشابه لـ fadeInUp)
      gsap.utils.toArray<HTMLElement>(".translateY10").forEach((el) => {
        const delay = parseFloat(el.dataset.delay || "0");
        if (isMobile) {
          // على الموبايل، نستخدم animation أبسط
          gsap.fromTo(
            el,
            { autoAlpha: 0 },
            {
              autoAlpha: 1,
              scrollTrigger: {
                trigger: el,
                start: "top 95%",
                toggleActions: "play none none none",
                once: true,
              },
              duration: 0.5,
              delay,
              ease: "power2.out",
            }
          );
        } else {
          gsap.fromTo(
            el,
            { y: 40, autoAlpha: 0 },
            {
              y: 0,
              autoAlpha: 1,
              scrollTrigger: {
                trigger: el,
                start: "top 95%",
                toggleActions: "play none none none",
              },
              duration: 0.6,
              delay,
              ease: "power2.out",
            }
          );
        }
      });

      // === CTA Circles ===
      gsap.utils.toArray<HTMLElement>(".cta-circles span").forEach((circle) => {
        let scale = 1;
        ScrollTrigger.create({
          trigger: circle,
          start: "top bottom",
          end: "bottom top",
          onUpdate: (self) => {
            scale =
              self.direction === 1
                ? gsap.utils.clamp(1, 1.2, scale + 0.01)
                : gsap.utils.clamp(1, 1.2, scale - 0.01);
            gsap.to(circle, {
              scale,
              overwrite: "auto",
              duration: 0.2,
              ease: "power3.out",
            });
          },
        });
      });

      // === Image Anim Left ===
      gsap.utils.toArray<HTMLElement>(".img-anim-left").forEach((el) => {
        const delay = parseFloat(el.dataset.delay || "0");
        gsap.fromTo(
          el,
          { xPercent: -8, clipPath: "inset(0 100% 0 0)", opacity: 0 },
          {
            xPercent: 0,
            clipPath: "inset(0 0% 0 0)",
            opacity: 1,
            duration: 1.2,
            ease: "power2.out",
            delay,
            scrollTrigger: { 
              trigger: el, 
              start: "top 95%", 
              toggleActions: "play none none none" 
            },
          }
        );
      });

      // === Image Anim Right ===
      gsap.utils.toArray<HTMLElement>(".img-anim-right").forEach((el) => {
        const delay = parseFloat(el.dataset.delay || "0");
        gsap.fromTo(
          el,
          { xPercent: 8, clipPath: "inset(0 0 0 100%)", opacity: 0 },
          {
            xPercent: 0,
            clipPath: "inset(0 0 0 0%)",
            opacity: 1,
            duration: 1.2,
            ease: "power2.out",
            delay,
            scrollTrigger: { 
              trigger: el, 
              start: "top 95%", 
              toggleActions: "play none none none" 
            },
          }
        );
      });

      // Image Zoom In/Out
      gsap.utils.toArray<HTMLElement>(".imgZoomInOut").forEach((el) => {
        let initialWidth = el.offsetWidth;
        const maxScale = 1.5;
        let maxAllowedWidth = window.innerWidth * 0.95;

        const resizeHandler = () => {
          maxAllowedWidth = window.innerWidth * 0.95;
          initialWidth = el.offsetWidth;
        };
        window.addEventListener("resize", resizeHandler);

        ScrollTrigger.create({
          trigger: el,
          start: "top 95%",
          scrub: true,
          onUpdate: (self) => {
            const progress = self.progress;
            const adjustedProgress = Math.max(0, (progress - 0.3) / 0.7);
            let newWidth = initialWidth * (1 + (maxScale - 1) * adjustedProgress);
            if (newWidth < initialWidth) newWidth = initialWidth;
            newWidth = Math.min(newWidth, maxAllowedWidth);
            el.style.width = `${newWidth}px`;
          },
        });

        // Cleanup on unmount
        return () => window.removeEventListener("resize", resizeHandler);
      });
    };

    // دالة لإزالة letter-spacing من العناصر المنشأة ديناميكياً - نسخة محسنة
    const fixArabicSpacing = (element: HTMLElement) => {
      if (document.documentElement.dir === "rtl" || document.documentElement.lang === "ar") {
        // استخدام setProperty مع important
        element.style.setProperty("letter-spacing", "normal", "important");
        element.style.setProperty("word-spacing", "normal", "important");
        // إزالة letter-spacing من جميع العناصر الداخلية
        element.querySelectorAll("*").forEach((el) => {
          const htmlEl = el as HTMLElement;
          htmlEl.style.setProperty("letter-spacing", "normal", "important");
          htmlEl.style.setProperty("word-spacing", "normal", "important");
        });
      }
    };

    // مراقبة العناصر الجديدة التي يتم إنشاؤها بواسطة GSAP - محسّن للموبايل
    const observer = new MutationObserver((mutations) => {
      // تقليل التكلفة على الموبايل
      if (isMobile) {
        // على الموبايل، نعالج فقط العناصر المضافة مباشرة
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              const element = node as HTMLElement;
              if (element.classList && (
                element.classList.contains("gsap-split-char") ||
                element.classList.contains("gsap-split-word") ||
                element.classList.contains("gsap-split-line")
              )) {
                fixArabicSpacing(element);
              }
            }
          });
        });
      } else {
        // على الديسكتوب، نستخدم المراقبة الكاملة
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              const element = node as HTMLElement;
              if (element.classList && (
                element.classList.contains("gsap-split-char") ||
                element.classList.contains("gsap-split-word") ||
                element.classList.contains("gsap-split-line") ||
                element.querySelector(".gsap-split-char, .gsap-split-word, .gsap-split-line")
              )) {
                fixArabicSpacing(element);
                element.querySelectorAll(".gsap-split-char, .gsap-split-word, .gsap-split-line, [class*='gsap-split']").forEach((el) => {
                  fixArabicSpacing(el as HTMLElement);
                });
              }
            }
          });
        });
      }
    });

    // إصلاح دوري للعناصر - أقل تكراراً على الموبايل
    if (!isMobile) {
      periodicFix = setInterval(() => {
        if (document.documentElement.dir === "rtl" || document.documentElement.lang === "ar") {
          document.querySelectorAll(".gsap-split-char, .gsap-split-word, .gsap-split-line, [class*='gsap-split'], .heading-chars, .heading-word, .heading-line").forEach((el) => {
            fixArabicSpacing(el as HTMLElement);
          });
        }
      }, 500); // كل 500ms بدلاً من 100ms
    }

    // Initialize on component mount
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => {
        initAnimations();
        // بدء مراقبة DOM بعد التحميل - محسّن للموبايل
        if (isMobile) {
          // على الموبايل، نراقب فقط التغييرات المباشرة
          observer.observe(document.body, {
            childList: true,
            subtree: false, // تقليل التكلفة
          });
        } else {
          observer.observe(document.body, {
            childList: true,
            subtree: true,
          });
        }
      });
    } else {
      initAnimations();
      // بدء مراقبة DOM - محسّن للموبايل
      if (isMobile) {
        observer.observe(document.body, {
          childList: true,
          subtree: false,
        });
      } else {
        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });
      }
    }

    // Refresh ScrollTrigger on window resize - محسّن للموبايل
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        ScrollTrigger.refresh();
        // إعادة إصلاح letter-spacing بعد refresh
        if (document.documentElement.dir === "rtl" || document.documentElement.lang === "ar") {
          document.querySelectorAll(".gsap-split-char, .gsap-split-word, .gsap-split-line, [class*='gsap-split']").forEach((el) => {
            const htmlEl = el as HTMLElement;
            fixArabicSpacing(htmlEl);
          });
        }
      }, isMobile ? 300 : 500); // وقت أقل على الموبايل
    };

    window.addEventListener("resize", handleResize, { passive: true });

    // Cleanup
    return () => {
      observer.disconnect();
      if (periodicFix) clearInterval(periodicFix);
      if (ctx) ctx.revert();
      window.removeEventListener("resize", handleResize);
      ScrollTrigger.getAll().forEach((st) => st.kill());
      if (smoother) smoother.kill();
    };
  }, []);

  return null;
}